import React, { type FC } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useOutletContext } from 'react-router-dom';

import KioskView from "./components/KioskView";
import ClientView from "./components/ClientView";
import AdminView from "./components/AdminView";
import { AuthProvider } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import MainLayout from "./MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./RoleProtectedRoute";
import DashboardPage from "./pages/DashboardPage";
import CreateQrPage from "./pages/CreateQrPage";
import ReportsPage from "./pages/ReportsPage";
import ClientsPage from "./pages/ClientsPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import ClientQrCodesPage from "./pages/ClientQrCodesPage";
import ClientScanPage from "./pages/ClientScanPage";
import { DataProvider } from "./context/DataContext";
import InvitePage from "./pages/InvitePage";
import NotConfirmedPage from "./pages/NotConfirmedPage";
import NotApprovedPage from "./pages/NotApprovedPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import {ConsentProvider} from "./context/ConsentContext";
import ConsentBanner from "./components/ConsentBanner";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import RecoverPasswordPage from "./pages/RecoverPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OneShotGroupsPage from "./pages/OneShotGroupsPage";
import ClientOneShotGroupsPage from "./pages/ClientOneShotGroupsPage";
import ClientScanPageBeta from "./pages/ClientScanPageBeta";
import ManualVerificationPage from "./pages/ManualVerificationPage";
import DevBanner from "./components/DevBanner";

// IMPORTA LA NUOVA PAGINA QUI
import BulkImportPage from "./pages/BulkImportPage";
import BadgeTemplatesPage from "./pages/BadgeTemplatesPage";
import BadgeDesignerPage from "./pages/BadgeDesignerPage";
import BadgeBatchPage from "./pages/BadgeBatchPage";
import GuidePage from "./pages/GuidePage";
import QrInfoLookupPage from "./pages/QrInfoLookupPage";

type OutletContextType = {
    handleScan: (isValid: boolean) => void;
};

export function useScanHandler() {
    return useOutletContext<OutletContextType>();
}

const App: FC = () => {
    return (
        <ConsentProvider>
            <AuthProvider>
                <BrowserRouter>
                    <DataProvider>
                        <ConsentBanner />
                        {process.env.REACT_APP_IS_DEV === "true" && (<DevBanner/>)}
                        <Routes>
                            {/* Rotte Pubbliche */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/invite/:code" element={<InvitePage />} />
                            <Route path="/notConfirmed/:token" element={<NotConfirmedPage />} />
                            <Route path="/notApproved" element={<NotApprovedPage />} />
                            <Route path="/confirm/:code" element={<ConfirmEmailPage />} />
                            <Route path="/unauthorized" element={<UnauthorizedPage />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                            <Route path="/recover-password" element={<RecoverPasswordPage />} />
                            <Route path="/recover/:token" element={<ResetPasswordPage />} />
                            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />

                            {/* Rotte Protette */}
                            <Route element={<ProtectedRoute />}>
                                <Route element={<MainLayout />}>
                                    <Route index element={<Navigate to="/admin/dashboard" replace />} />
                                    <Route path="admin" element={<AdminView />}>
                                        <Route index element={<Navigate to="dashboard" replace />} />
                                        <Route path="dashboard" element={<DashboardPage />} />
                                        <Route path="oneshots" element={<OneShotGroupsPage />} />
                                        <Route path="scans" element={<ClientScanPage />} />
                                        {/*<Route path="scansbeta" element={<ClientScanPageBeta />} />*/}
                                        <Route path="info" element={<QrInfoLookupPage />} />
                                        <Route path="profile" element={<ProfilePage />} />

                                        <Route path="badges" element={<BadgeTemplatesPage />} />
                                        <Route path="badge-designer" element={<BadgeDesignerPage />} />
                                        <Route path="badge-designer/:id" element={<BadgeDesignerPage />} />
                                        <Route path="badge-generate/:id" element={<BadgeBatchPage />} />

                                        <Route path="guide" element={<GuidePage />} />

                                        <Route element={<RoleProtectedRoute allowedRoles={['ADMIN', 'STAFF']} allowedClient={false} allowedInternal={true} />}>
                                            <Route path="create" element={<CreateQrPage />} />
                                            <Route path="update/:id" element={<CreateQrPage />} />
                                            <Route path="updateshot/:idshot" element={<CreateQrPage />} />

                                            {/* NUOVA ROTTA: IMPORTAZIONE MASSIVA */}
                                            <Route path="bulk-import" element={<BulkImportPage />} />

                                            <Route path="reports" element={<ReportsPage />} />
                                            <Route path="manual" element={<ManualVerificationPage />} />
                                        </Route>

                                        <Route element={<RoleProtectedRoute allowedRoles={['ADMIN']} allowedClient={false} allowedInternal={true} />}>
                                            <Route path="clients" element={<ClientsPage />} />
                                            <Route path="users" element={<UsersPage />} />
                                        </Route>
                                    </Route>
                                    <Route path="client" element={<ClientView />}>
                                        <Route index element={<Navigate to="qrcodes" replace />} />
                                        <Route path="qrcodes" element={<ClientQrCodesPage />} />
                                        <Route path="oneshots" element={<ClientOneShotGroupsPage />} />
                                        <Route path="scan" element={<ClientScanPage />} />
                                        {/*<Route path="scanbeta" element={<ClientScanPageBeta />} />*/}
                                        <Route path="info" element={<QrInfoLookupPage />} />
                                        <Route path="profile" element={<ProfilePage />} />
                                        <Route path="badges" element={<BadgeTemplatesPage />} />
                                        <Route path="badge-designer" element={<BadgeDesignerPage />} />
                                        <Route path="badge-designer/:id" element={<BadgeDesignerPage />} />
                                        <Route path="badge-generate/:id" element={<BadgeBatchPage />} />
                                    </Route>
                                    <Route path="kiosk" element={<KioskView />} />
                                </Route>
                            </Route>
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </DataProvider>
                </BrowserRouter>
            </AuthProvider>
        </ConsentProvider>
    );
}

export default App;